import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Zap, Edit, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService, FillerConfig, UpdateFillerConfigRequest, LLMProvider, LLMModel } from '../../services/api';
import { toast } from 'sonner';

export function FillerSettingsPage() {
  const [config, setConfig] = useState<FillerConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [formData, setFormData] = useState<UpdateFillerConfigRequest>({
    provider: 'openai',
    model: 'gpt-4o-mini',
    prompt: '',
    temperature: 0.7,
    maxTokens: 100,
    enabled: true,
  });

  useEffect(() => {
    loadConfig();
    loadProvidersAndModels();
  }, []);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getFillerConfig();
      setConfig(data);
      setFormData({
        provider: data.provider,
        model: data.model,
        prompt: data.prompt,
        temperature: data.temperature,
        maxTokens: data.maxTokens,
        enabled: data.enabled,
      });
    } catch (err: any) {
      console.error('Failed to load filler config:', err);
      setError('Filler-Einstellungen konnten nicht geladen werden');
      toast.error('Einstellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProvidersAndModels = async () => {
    try {
      const [providersData, modelsData] = await Promise.all([
        apiService.getLlmProviders(false),
        apiService.getLlmModels({ includeInactive: false }),
      ]);
      setProviders(providersData.filter(p => p.status === 'active' && p.name));
      setModels(modelsData.filter(m => m.status === 'active' && m.modelKey));
    } catch (err: any) {
      console.error('Failed to load providers and models:', err);
      toast.error('Provider und Modelle konnten nicht geladen werden');
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await apiService.updateFillerConfig(formData);
      toast.success('Filler-Einstellungen gespeichert');
      await loadConfig();
      setIsDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to save filler config:', err);
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditDialog = () => {
    if (config) {
      setFormData({
        provider: config.provider,
        model: config.model,
        prompt: config.prompt,
        temperature: config.temperature,
        maxTokens: config.maxTokens,
        enabled: config.enabled,
      });
    }
    setIsDialogOpen(true);
  };

  // Get models for a specific provider from the loaded data
  const getProviderModels = useMemo(() => {
    return (providerName: string) => {
      const provider = providers.find(p => p.name === providerName);
      if (!provider) return [];

      return models
        .filter(m => m.providerId === provider.id)
        .sort((a, b) => (a.displayName || a.modelKey).localeCompare(b.displayName || b.modelKey))
        .map(m => ({
          value: m.modelKey,
          label: m.displayName || m.modelKey,
        }));
    };
  }, [providers, models]);

  // Get provider display name from loaded data
  const getProviderLabel = (providerName: string) => {
    const provider = providers.find(p => p.name === providerName);
    return provider?.displayName || providerName;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Lade Einstellungen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6" />
            Filler Einstellungen
          </h1>
          <p className="text-gray-600">Konfigurieren Sie die Filler-Überbrückungsphrasen</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadConfig}>Erneut versuchen</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6" />
            Filler Einstellungen
          </h1>
          <p className="text-gray-600">
            Konfigurieren Sie die Überbrückungsphrasen während Tool-Ausführungen
          </p>
        </div>
        <Button onClick={openEditDialog}>
          <Edit className="w-4 h-4 mr-2" />
          Bearbeiten
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Was sind Filler-Responses?</h3>
              <p className="text-sm text-blue-700 mt-1">
                Filler-Responses sind kurze Überbrückungsphrasen wie "Einen Moment bitte, ich schaue nach...",
                die während der Ausführung von Tools generiert werden. Sie verbessern die Benutzererfahrung,
                indem sie natürliche Wartezeiten simulieren.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Aktuelle Konfiguration</CardTitle>
              <CardDescription>
                Diese Einstellungen gelten global für alle Agenten
              </CardDescription>
            </div>
            <Badge variant={config?.enabled ? 'default' : 'secondary'}>
              {config?.enabled ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Aktiviert
                </>
              ) : (
                'Deaktiviert'
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Provider</Label>
                <p className="text-lg font-medium">{getProviderLabel(config?.provider || '')}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Modell</Label>
                <p className="text-lg font-medium font-mono">{config?.model}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-500">Temperatur</Label>
                <p className="text-lg font-medium">{config?.temperature}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-500">Max Tokens</Label>
                <p className="text-lg font-medium">{config?.maxTokens}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <Label className="text-sm font-medium text-gray-500">System Prompt</Label>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{config?.prompt}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Filler-Einstellungen bearbeiten</DialogTitle>
            <DialogDescription>
              Passen Sie die Filler-Konfiguration an Ihre Anforderungen an
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Enabled Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Filler aktivieren</Label>
                <p className="text-sm text-gray-500">
                  Überbrückungsphrasen während Tool-Ausführungen generieren
                </p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>

            {/* Provider Selection */}
            <div className="space-y-2">
              <Label htmlFor="provider">LLM Provider</Label>
              <Select
                value={formData.provider}
                onValueChange={(value) => {
                  const providerModels = getProviderModels(value);
                  setFormData({
                    ...formData,
                    provider: value,
                    model: providerModels.length > 0 ? providerModels[0].value : ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Provider auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {providers.length === 0 ? (
                    <SelectItem value="_loading" disabled>Laden...</SelectItem>
                  ) : (
                    providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.name || provider.id}>
                        {provider.displayName || provider.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Model Selection */}
            <div className="space-y-2">
              <Label htmlFor="model">Modell</Label>
              <Select
                value={formData.model}
                onValueChange={(value) => setFormData({ ...formData, model: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Modell auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const providerModels = getProviderModels(formData.provider || '');
                    if (providerModels.length === 0) {
                      return <SelectItem value="_none" disabled>Keine Modelle verfügbar</SelectItem>;
                    }
                    return providerModels.map((m: { value: string; label: string }) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Für Filler empfehlen wir schnelle, kostengünstige Modelle
              </p>
            </div>

            {/* Temperature */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="temperature">Temperatur</Label>
                <span className="text-sm text-gray-500 font-mono">{formData.temperature}</span>
              </div>
              <Input
                id="temperature"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Präzise (0)</span>
                <span>Kreativ (1)</span>
              </div>
            </div>

            {/* Max Tokens */}
            <div className="space-y-2">
              <Label htmlFor="maxTokens">Max Tokens</Label>
              <Input
                id="maxTokens"
                type="number"
                min="10"
                max="500"
                value={formData.maxTokens}
                onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) || 100 })}
              />
              <p className="text-xs text-gray-500">
                Empfohlen: 50-150 Tokens für kurze Überbrückungsphrasen
              </p>
            </div>

            {/* System Prompt */}
            <div className="space-y-2">
              <Label htmlFor="prompt">System Prompt</Label>
              <Textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="Anweisungen für die Generierung von Filler-Phrasen..."
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Dieser Prompt steuert, wie Filler-Phrasen generiert werden
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
